import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { EVENT_PACKS, PACKS, T } from "@/data/content";
import { FadeUp, SectionLabel, BlurBackdrop } from "@/components/Reveal";
import { Calculator, Megaphone, PartyPopper, Plus, Wallet, CreditCard, Loader2, Scale, Check, Camera } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const fr = (n) => `${new Intl.NumberFormat("fr-CH").format(n)}.-`;

const API = process.env.REACT_APP_BACKEND_URL;

// acompte Stripe indicatif par pack (carte uniquement)
const PACK_DEPOSIT = {
    pack1: { key: "acompte_300", amount: 300 },
    pack2: { key: "acompte_750", amount: 750 },
    pack3: { key: "acompte_1500", amount: 1500 },
    ev1: { key: "acompte_ev500", amount: 500 },
    ev2: { key: "acompte_ev750", amount: 750 },
    ev3: { key: "acompte_ev600", amount: 600 },
};

export function PayPackButton({ packId, testid, withRights = false }) {
    const { lang } = useLang();
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(false);
    const tier = PACK_DEPOSIT[packId];
    const amount = tier ? (withRights ? tier.amount * 3 : tier.amount) : 0;
    const pay = async () => {
        setBusy(true);
        setErr(false);
        try {
            const r = await fetch(`${API}/api/payments/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ lookup_key: tier.key, origin_url: window.location.origin, booking_ref: `Acompte ${packId.toUpperCase()}${withRights ? " + DROITS" : ""}`, with_rights: withRights }),
            });
            const d = await r.json();
            if (!r.ok || !d.checkout_url) throw new Error();
            window.location.href = d.checkout_url;
        } catch {
            setErr(true);
            setBusy(false);
        }
    };
    return (
        <>
            <button
                data-testid={testid}
                onClick={pay}
                disabled={busy}
                className="mt-6 flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 py-3.5 font-mono text-[10px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-40"
            >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                {tx(lang, T.pricing.pay)} — {amount} CHF
            </button>
            {withRights && <p data-testid={`${testid}-rights-note`} className="mt-2 text-center font-mono text-[8px] tracking-[0.2em] text-gold">{lang === "fr" ? "DROITS COMPLETS INCLUS (+200%)" : "FULL RIGHTS INCLUDED (+200%)"}</p>}
            {err && <p className="mt-2 text-center font-mono text-[8px] tracking-[0.2em] text-blood">ERREUR — RÉESSAYEZ</p>}
        </>
    );
}

const URGENCY = {
    flexible: { fee: 0.12, k: { en: "FLEXIBLE — DISCOUNT", fr: "FLEXIBLE — REMISE" } },
    month: { fee: 0, k: { en: "THIS MONTH", fr: "SOUS 1 MOIS" } },
    fortnight: { fee: 0.12, k: { en: "2 – 3 WEEKS", fr: "2 – 3 SEMAINES" } },
    week: { fee: 0.25, k: { en: "UNDER 1 WEEK — RUSH", fr: "MOINS D'1 SEMAINE — RUSH" } },
};

const Row = ({ label, children }) => (
    <div className="border-t border-white/10 py-5">
        <p className="mb-3 font-mono text-[10px] tracking-[0.3em] text-cream/50">{label}</p>
        {children}
    </div>
);

// calculateur droits d'exploitation (+200%) — réutilisé par packs clips et événements
function RightsBlock({ base, lang, T, tid, onChange }) {
    const [withRights, setWithRights] = useState(false);
    const set = (v) => { setWithRights(v); onChange?.(v); };
    const rights = base * 2;
    const total = base * 3;
    return (
        <div data-testid={tid.box} className="mt-6 border-t border-white/10 pt-5">
            <p className="font-mono text-[8px] tracking-[0.25em] text-gold">{tx(lang, T.pricing.rightsTitle)}</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                    data-testid={tid.no}
                    onClick={() => set(false)}
                    className={`border px-2 py-2 font-mono text-[8px] tracking-[0.12em] transition-all ${!withRights ? "border-violet bg-violet/10 text-violet" : "border-white/15 text-cream/50 hover:border-white/40"}`}
                >
                    {tx(lang, T.pricing.rightsChoiceNo)}
                </button>
                <button
                    data-testid={tid.yes}
                    onClick={() => set(true)}
                    className={`border px-2 py-2 font-mono text-[8px] tracking-[0.12em] transition-all ${withRights ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-cream/50 hover:border-white/40"}`}
                >
                    {tx(lang, T.pricing.rightsChoiceYes)}
                </button>
            </div>
            {/* calcul détaillé */}
            <div className="mt-3 space-y-1 font-mono text-[9px] tracking-[0.15em]">
                <p className="flex justify-between text-cream/60"><span>BASE</span><span>{fr(base)} CHF</span></p>
                <p className={`flex justify-between ${withRights ? "text-gold" : "text-cream/30"}`}>
                    <span>+200% {lang === "fr" ? "DROITS" : "RIGHTS"}</span><span>{withRights ? `+${fr(rights)} CHF` : "—"}</span>
                </p>
                <p className="flex justify-between border-t border-white/10 pt-1 font-bold text-cream">
                    <span>{tx(lang, T.pricing.rightsTotal)}</span>
                    <span data-testid={tid.total} className={withRights ? "text-gold" : "text-violet"}>{fr(withRights ? total : base)} CHF</span>
                </p>
            </div>
        </div>
    );
}

// carte pack avec choix droits d'exploitation (+200%) et calcul détaillé
function PackRightsCard({ p, i, lang, T }) {
    const base = typeof p.price === "number" ? p.price : null;
    const [withRights, setWithRights] = useState(false);
    return (
        <FadeUp key={p.id} delay={i * 0.08}>
            <div
                data-testid={`pack-card-${p.id}`}
                className={`group relative flex h-full flex-col border p-7 transition-colors duration-500 md:p-9 ${
                    p.featured ? "border-violet/60 bg-violet/[0.06]" : "border-white/10 hover:border-white/30"
                }`}
            >
                {p.featured && (
                    <span className="absolute -top-3 left-7 bg-violet px-3 py-1 font-mono text-[9px] tracking-[0.3em] text-cream">
                        {tx(lang, T.pricing.popular)}
                    </span>
                )}
                <p className="font-mono text-[10px] tracking-[0.3em] text-cream/50">{tx(lang, p.name)}</p>
                <p className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                    {tx(lang, p.from)}
                    <span className="text-violet"> {fr(p.price)}</span>
                </p>
                <ul className="mt-6 flex-1 space-y-3">
                    {p.items.map((it, j) => (
                        <li key={j} className="flex items-start gap-3 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-cream/60">
                            <Plus size={11} className="mt-0.5 shrink-0 text-gold" />
                            {tx(lang, it)}
                        </li>
                    ))}
                </ul>
                {/* choix droits d'exploitation */}
                {base != null && (
                    <RightsBlock base={base} lang={lang} T={T} onChange={setWithRights} tid={{
                        box: `pack-rights-${p.id}`,
                        no: `pack-rights-no-${p.id}`,
                        yes: `pack-rights-yes-${p.id}`,
                        total: `pack-total-${p.id}`,
                    }} />
                )}
                <div className="mt-4">
                    <PayPackButton packId={p.id} testid={`pack-pay-${p.id}`} withRights={withRights} />
                </div>
            </div>
        </FadeUp>
    );
}

// carte pack événement — droits remontés vers le bouton d'acompte
function EventPackCard({ p, i, lang, T }) {
    const [withRights, setWithRights] = useState(false);
    return (
        <FadeUp delay={i * 0.08}>
            <div
                data-testid={`event-pack-${p.id}`}
                className={`group relative flex h-full flex-col border p-7 transition-colors duration-500 md:p-9 ${
                    p.featured
                        ? "border-gold/60 bg-gold/[0.06]"
                        : "border-white/10 hover:border-white/30"
                }`}
            >
                {p.featured && (
                    <span className="absolute -top-3 left-7 bg-gold px-3 py-1 font-mono text-[9px] tracking-[0.3em] text-ink">
                        {tx(lang, T.pricing.popular)}
                    </span>
                )}
                <p className="font-mono text-[10px] tracking-[0.3em] text-cream/50">
                    {tx(lang, p.name)}
                </p>
                <p className="mt-4 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                    {p.price}
                    <span className="text-violet"> CHF</span>
                </p>
                <ul className="mt-6 flex-1 space-y-3">
                    {p.items.map((it, j) => (
                        <li
                            key={j}
                            className="flex items-start gap-3 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-cream/60"
                        >
                            <Plus size={11} className="mt-0.5 shrink-0 text-gold" />
                            {tx(lang, it)}
                        </li>
                    ))}
                </ul>
                {/* droits d'exploitation — calculé sur le prix de base */}
                {p.basePrice && (
                    <RightsBlock base={p.basePrice} lang={lang} T={T} onChange={setWithRights} tid={{
                        box: `event-rights-${p.id}`,
                        no: `event-rights-no-${p.id}`,
                        yes: `event-rights-yes-${p.id}`,
                        total: `event-total-${p.id}`,
                    }} />
                )}
                {/* acompte Stripe direct — comme les packs clips */}
                <PayPackButton packId={p.id} testid={`event-pay-${p.id}`} withRights={withRights} />
            </div>
        </FadeUp>
    );
}

export default function Pricing() {
    const { lang } = useLang();
    const [dur, setDur] = useState("30");
    const [hours, setHours] = useState("2");
    const [locs, setLocs] = useState("2");
    const [da, setDa] = useState("no");
    const [release, setRelease] = useState("flexible");

    const estimate = useMemo(() => {
        let base = hours === "2" ? 750 : hours === "5" ? 1200 : 2000;
        const notes = [];
        const locExtra = { "2": 0, "4": 150, "7": 350 }[locs];
        const durExtra = { "30": 0, "60": 100, "100": 250 }[dur];
        const daExtra = { no: 0, light: 200, full: 600 }[da];
        if (locExtra) notes.push(`+${fr(locExtra)} ${tx(lang, T.pricing.optLocs)} ${locs}`);
        if (durExtra) notes.push(`+${fr(durExtra)} ${tx(lang, T.pricing.optDur)} ${dur.replace("30", "2:30").replace("60", "3:10").replace("100", "4:10")}`);
        if (daExtra) notes.push(`+${fr(daExtra)} ${tx(lang, T.pricing.optDa)}`);
        const urgency = URGENCY[release];
        const fee = Math.round((base + locExtra + durExtra + daExtra) * urgency.fee);
        const total = base + locExtra + durExtra + daExtra + (release === "flexible" ? -fee : fee);
        return { total, notes, fee, urgency };
    }, [dur, hours, locs, da, release, lang]);

    const Chip = ({ active, onClick, children, testid }) => (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`border px-4 py-2 font-mono text-[10px] tracking-[0.2em] transition-all duration-300 ${
                active
                    ? "border-violet bg-violet text-cream"
                    : "border-white/15 text-cream/60 hover:border-cream hover:text-cream"
            }`}
        >
            {children}
        </button>
    );

    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="relative overflow-hidden bg-[#101014] py-24 text-cream md:py-36"
        >
            <BlurBackdrop src="/videos/davido-reel-1.jpg" tint="bg-[#101014]/80" />
            <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
                <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <SectionLabel en={tx(lang, T.pricing.label)} fr={tx(lang, T.pricing.label)} />
                        <FadeUp delay={0.1}>
                            <h2 className="mt-8 font-display text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                                {tx(lang, T.pricing.title)}
                                <span className="text-violet">.</span>
                            </h2>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.2}>
                        <p className="max-w-xs font-mono text-[9px] leading-relaxed tracking-[0.3em] text-cream/40 md:text-[10px]">
                            {tx(lang, T.pricing.note)}
                        </p>
                    </FadeUp>
                </div>

                {/* packs */}
                <div className="mt-14 grid grid-cols-1 gap-5 md:mt-20 md:grid-cols-3 md:gap-8">
                    {PACKS.map((p, i) => (
                        <PackRightsCard key={p.id} p={p} i={i} lang={lang} T={T} />
                    ))}
                </div>

                {/* event packs */}
                <FadeUp delay={0.05} className="mt-20 md:mt-28">
                    <div className="flex flex-col justify-between gap-6 border-t border-white/10 pt-12 md:flex-row md:items-end">
                        <div>
                            <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] text-gold">
                                <PartyPopper size={13} /> {tx(lang, T.pricing.eventsLabel)}
                            </p>
                            <h3 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight md:text-5xl">
                                {tx(lang, T.pricing.eventsTitle)}
                                <span className="text-violet">.</span>
                            </h3>
                        </div>
                        <p className="max-w-xs font-mono text-[9px] leading-relaxed tracking-[0.3em] text-cream/40 md:text-[10px]">
                            {tx(lang, T.pricing.eventsNote)}
                        </p>
                    </div>
                    <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
                        {EVENT_PACKS.map((p, i) => (
                            <EventPackCard key={p.id} p={p} i={i} lang={lang} T={T} />
                        ))}
                    </div>
                </FadeUp>

                {/* cadrage uniquement — 180/h + droits */}
                <FadeUp delay={0.05} className="mt-20 md:mt-28">
                    <div className="border border-gold/30 bg-gold/[0.04] p-7 md:p-10">
                        <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] text-gold">
                            <Camera size={13} /> {tx(lang, T.pricing.cadrageLabel)}
                        </p>
                        <h3 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
                            {tx(lang, T.pricing.cadrageTitle)}
                        </h3>
                        <p className="mt-4 max-w-2xl font-mono text-[9px] leading-relaxed tracking-[0.25em] text-cream/50">
                            {tx(lang, T.pricing.cadrageSub)}
                        </p>
                        <p data-testid="cadrage-example" className="mt-5 border-l-2 border-gold pl-4 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-gold">
                            {tx(lang, T.pricing.cadrageExample)}
                        </p>
                    </div>
                </FadeUp>

                {/* droits d'exploitation — texte légal détaillé */}
                <FadeUp delay={0.05} className="mt-20 md:mt-28">
                    <div className="border-t border-white/10 pt-12">
                        <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] text-violet">
                            <Scale size={13} /> {tx(lang, T.pricing.rightsTitle)}
                        </p>
                        <div className="mt-8 grid gap-10 md:grid-cols-2">
                            <div>
                                <p className="font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/60">
                                    {tx(lang, T.pricing.rightsBase)}
                                </p>
                                <p className="mt-6 font-mono text-[10px] font-bold tracking-[0.25em] text-gold">
                                    {tx(lang, T.pricing.rightsFullTitle)}
                                </p>
                                <p className="mt-3 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/60">
                                    {tx(lang, T.pricing.rightsFullIntro)}
                                </p>
                                <ul className="mt-4 space-y-3">
                                    {T.pricing.rightsFullItems.map((it, j) => (
                                        <li key={j} className="flex items-start gap-3 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-cream/60">
                                            <Check size={11} className="mt-0.5 shrink-0 text-gold" />
                                            {tx(lang, it)}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-4 border-l-2 border-gold pl-4 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-gold">
                                    {tx(lang, T.pricing.rightsFullNote)}
                                </p>
                            </div>
                            <div>
                                <p className="font-mono text-[10px] font-bold tracking-[0.25em] text-cream">
                                    {tx(lang, T.pricing.rightsProcessTitle)}
                                </p>
                                <p className="mt-3 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/60">
                                    {tx(lang, T.pricing.rightsProcess)}
                                </p>
                                <p className="mt-6 font-mono text-[10px] font-bold tracking-[0.25em] text-cream">
                                    {tx(lang, T.pricing.rightsWhyTitle)}
                                </p>
                                <p className="mt-3 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/60">
                                    {tx(lang, T.pricing.rightsWhy)}
                                </p>
                            </div>
                        </div>
                    </div>
                </FadeUp>

                {/* estimator */}
                <div id="estimator" className="scroll-mt-24">
                <FadeUp delay={0.1} className="mt-20 md:mt-28">
                    <div className="grid grid-cols-1 gap-10 border-t border-white/10 pt-12 lg:grid-cols-12">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3">
                                <Calculator size={16} className="text-gold" />
                                <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-4xl">
                                    {tx(lang, T.pricing.estTitle)}
                                </h3>
                            </div>
                            <p className="mt-3 font-mono text-[10px] tracking-[0.3em] text-cream/40">
                                {tx(lang, T.pricing.estSub)}
                            </p>
                            <div className="mt-8">
                                <Row label={tx(lang, T.pricing.qDur)}>
                                    <div className="flex flex-wrap gap-2">
                                        {["30", "60", "100"].map((v) => (
                                            <Chip key={v} testid={`est-dur-${v}`} active={dur === v} onClick={() => setDur(v)}>
                                                {v === "30" ? "≤ 2:30" : v === "60" ? "≤ 3:10" : "≤ 4:10"}
                                            </Chip>
                                        ))}
                                    </div>
                                </Row>
                                <Row label={tx(lang, T.pricing.qHours)}>
                                    <div className="flex flex-wrap gap-2">
                                        {["2", "5", "8"].map((v) => (
                                            <Chip key={v} testid={`est-hours-${v}`} active={hours === v} onClick={() => setHours(v)}>
                                                {v === "2" ? "2H" : v === "5" ? "4 – 5H" : "6 – 8H"}
                                            </Chip>
                                        ))}
                                    </div>
                                </Row>
                                <Row label={tx(lang, T.pricing.qLocs)}>
                                    <div className="flex flex-wrap gap-2">
                                        {["2", "4", "7"].map((v) => (
                                            <Chip key={v} testid={`est-locs-${v}`} active={locs === v} onClick={() => setLocs(v)}>
                                                {v === "2" ? "1 – 2" : v === "4" ? "3 – 4" : "5 – 7"}
                                            </Chip>
                                        ))}
                                    </div>
                                </Row>
                                <Row label={tx(lang, T.pricing.qDa)}>
                                    <div className="flex flex-wrap gap-2">
                                        {["no", "light", "full"].map((v) => (
                                            <Chip key={v} testid={`est-da-${v}`} active={da === v} onClick={() => setDa(v)}>
                                                {tx(lang, T.pricing[`da_${v}`])}
                                            </Chip>
                                        ))}
                                    </div>
                                </Row>
                                <Row label={tx(lang, T.pricing.qRelease)}>
                                    <div className="flex flex-wrap gap-2">
                                        {["flexible", "month", "fortnight", "week"].map((v) => (
                                            <Chip key={v} testid={`est-release-${v}`} active={release === v} onClick={() => setRelease(v)}>
                                                {tx(lang, URGENCY[v].k)}
                                            </Chip>
                                        ))}
                                    </div>
                                </Row>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="sticky top-28 border border-gold/40 bg-ink/60 p-7 backdrop-blur-sm md:p-9">
                                <p className="font-mono text-[10px] tracking-[0.3em] text-cream/50">
                                    {tx(lang, T.pricing.estResult)}
                                </p>
                                <div className="mt-6 border-t border-gold/20 pt-5">
                                    <p className="font-mono text-[10px] tracking-[0.3em] text-gold/70">
                                        {tx(lang, T.pricing.deposit)}
                                    </p>
                                </div>
                                <p
                                    data-testid="estimator-price"
                                    className="mt-4 font-display text-5xl font-extrabold tracking-tight text-gold md:text-6xl"
                                >
                                    {fr(estimate.total)}
                                </p>
                                <AnimatePresence mode="popLayout">
                                    <motion.ul
                                        key={estimate.notes.join("|") + estimate.total}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mt-6 space-y-2"
                                    >
                                        {estimate.notes.map((n, i) => (
                                            <li key={i} className="font-mono text-[10px] tracking-[0.2em] text-cream/50">
                                                {n}
                                            </li>
                                        ))}
                                        {estimate.fee > 0 && (
                                            <li className="font-mono text-[10px] tracking-[0.2em] text-violet">
                                                {release === "flexible" ? "−" : "+"}
                                                {fr(estimate.fee)} — {tx(lang, estimate.urgency.k)}
                                            </li>
                                        )}
                                    </motion.ul>
                                </AnimatePresence>
                                <p className="mt-8 border-t border-white/10 pt-6 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-cream/40">
                                    {tx(lang, T.pricing.disclaimer)}
                                </p>
                            </div>
                        </div>
                    </div>
                </FadeUp>

                {/* ads */}
                <FadeUp delay={0.1} className="mt-20 md:mt-28">
                    <div
                        data-testid="ads-banner"
                        className="relative overflow-hidden border border-white/10 bg-gradient-to-r from-violet/15 via-ink to-gold/10 p-8 md:p-12"
                    >
                        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                            <div>
                                <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-gold">
                                    <Megaphone size={14} />
                                    {tx(lang, T.pricing.adsLabel)}
                                </p>
                                <h3 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight md:text-5xl">
                                    {tx(lang, T.pricing.adsTitle)}
                                </h3>
                                <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-cream/60">
                                    {tx(lang, T.pricing.adsBody)}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {tx(lang, T.pricing.adsTags).split(" — ").map((t) => (
                                    <span
                                        key={t}
                                        className="border border-white/20 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-cream/70"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </FadeUp>
                </div>
            </div>
        </section>
    );
}
